import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { TMaterializedCitation } from '@/services/types/agent-chat-types';

export interface SelectedCitation {
  materializedCitation: TMaterializedCitation;
  selectedBlockId: string;
}

export interface AgentChatContextType {
  selectedThreadId: string | null;
  setSelectedThreadId: (threadId: string | null) => void;
  selectedCitation: SelectedCitation | null;
  setSelectedCitation: (citation: SelectedCitation | null) => void;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  mobileView: 'list' | 'chat';
  setMobileView: (view: 'list' | 'chat') => void;
}

const AgentChatContext = createContext<AgentChatContextType | undefined>(
  undefined,
);

const SIDEBAR_STORAGE_KEY = 'agent-chat-sidebar-open';

export const AgentChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedCitation, setSelectedCitation] =
    useState<SelectedCitation | null>(null);

  // UI state with localStorage persistence
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const [mobileView, setMobileView] = useState<'list' | 'chat'>('list');

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarOpen));
  }, [isSidebarOpen]);

  // Clear citation when thread changes
  useEffect(() => {
    setSelectedCitation(null);
  }, [selectedThreadId]);

  const contextValue = useMemo(
    () => ({
      selectedThreadId,
      setSelectedThreadId,
      selectedCitation,
      setSelectedCitation,
      isSidebarOpen,
      setIsSidebarOpen,
      mobileView,
      setMobileView,
    }),
    [selectedThreadId, isSidebarOpen, mobileView, selectedCitation],
  );

  return (
    <AgentChatContext.Provider value={contextValue}>
      {children}
    </AgentChatContext.Provider>
  );
};

export const useAgentChat = () => {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
};

export const useOptionalAgentChat = () => useContext(AgentChatContext);
